/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   ใบส่งมอบ FG (ฝ่ายผลิต → คลังสินค้า)

   เลือกได้เฉพาะ Production Order ที่มีใบเบิกวัตถุดิบ "ตัดสต๊อกแล้ว"
   สร้าง → "รอยืนยัน" แก้จำนวน / วันผลิต / วันหมดอายุได้
   กด "ยืนยันรับเข้าคลัง" → FG เข้าสต๊อกเป็น lot (เลข lot = เลขที่ใบ-ลำดับ)
   วันหมดอายุคำนวณให้จาก shelf life ของ FG เมื่อกรอกวันผลิต (แก้ทับได้)
============================================================ */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Card, Form, Flex, Row, Col, Space, Input, InputNumber, Button, Table,
  Typography, DatePicker, Select, Modal, Popconfirm, Tag, message,
} from "antd";
import {
  ArrowLeftOutlined, SaveFilled, CheckCircleOutlined, StopOutlined, ReloadOutlined, PlusOutlined,
} from "@ant-design/icons";
import { RiDeleteBin5Line } from "react-icons/ri";
import dayjs from "dayjs";
import ModalItemPicker from "../../../components/modal/ERP/item-picker/ModalItemPicker";
import OptionService from "../../../service/ERP/Options.service";

import FgHandoverService from "../../../service/ERP/FgHandover.service";
import useGoBack from "../../../hooks/useGoBack";
import { FH_PENDING, FH_CONFIRMED, FH_CANCELLED, StatusTag, num, fdate } from "./model";
import ActionGroup from "../../../components/action-group/ActionGroup";

const fhService = FgHandoverService();
/* ปุ่มหัวหน้าใช้ขนาดเดียวกับแถวปุ่มของหน้า Production Order */
const ACTION_BTN = { minWidth: "9.5rem", height: 36, paddingInline: 12 };
const errMsg = (err, d) => err?.response?.data?.message || d;
const toDay = (v) => (v ? dayjs(v) : null);
const ymd = (v) => (v ? dayjs(v).format("YYYY-MM-DD") : null);

export default function FgHandoverManage() {
  const location = useLocation();
  const goBack = useGoBack("/ERP/fg-handover");
  const config = location.state?.config || { action: "create" };

  const [form] = Form.useForm();
  const [header, setHeader] = useState({});
  const [items, setItems] = useState([]);
  const [pdOptions, setPdOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [openPicker, setOpenPicker] = useState(false);

  const addFg = (r) => {
    if (items.some((x) => x.fg_code === r.stcode)) {
      message.warning("มี FG นี้ในใบส่งมอบแล้ว");
      return;
    }
    setItems((prev) => [
      ...prev,
      { _key: `${r.stcode}-${Date.now()}`, fg_code: r.stcode, fg_name: r.stname, unit: r.unit,
        plan_qty: 0, qty: 0, mfg_date: null, expire_date: null },
    ]);
  };

  const isCreate = config.action === "create";
  const status = header.doc_status || FH_PENDING;
  const editable = config.action !== "view" && status === FH_PENDING;

  const withKeys = (rows) => rows.map((r, i) => ({ ...r, _key: `${r.fg_code}-${i}` }));

  const applyLoaded = (data) => {
    const h = data?.header || {};
    setHeader(h);
    setItems(withKeys(data?.items || []));
    form.setFieldsValue({ pdcode: h.pdcode, fhdate: h.fhdate ? dayjs(h.fhdate) : dayjs(), remark: h.remark });
  };

  const loadPrefill = (pdcode) => {
    if (!pdcode) return;
    fhService
      .prefill(pdcode)
      .then((res) => setItems(withKeys(res.data?.data?.items || [])))
      .catch((err) => {
        setItems([]);
        message.error(errMsg(err, "ดึงรายการ FG ไม่สำเร็จ"));
      });
  };

  const searchPd = (q) =>
    fhService
      .pdOptions(q || "")
      .then((res) =>
        setPdOptions(
          (res.data?.data || []).map((p) => ({
            value: p.pdcode,
            label: `${p.pdcode}${p.pddate ? ` (${fdate(p.pddate)})` : ""}`,
          }))
        )
      )
      .catch(() => {});

  useEffect(() => {
    if (!isCreate && config.code) {
      fhService
        .get(config.code)
        .then((res) => applyLoaded(res.data?.data))
        .catch((err) => message.error(errMsg(err, "โหลดใบส่งมอบไม่สำเร็จ")));
    } else {
      form.setFieldsValue({ fhdate: dayjs(), pdcode: config.pdcode || undefined });
      if (config.pdcode) {
        setPdOptions([{ value: config.pdcode, label: config.pdcode }]);
        loadPrefill(config.pdcode);
      }
    }
  }, []);

  const updateItem = (key, patch) =>
    setItems((prev) => prev.map((r) => (r._key === key ? { ...r, ...patch } : r)));

  /* กรอกวันผลิต → เติมวันหมดอายุจาก shelf life ให้ */
  const onMfgChange = (r, d) => {
    const patch = { mfg_date: ymd(d) };
    if (d && Number(r.shelf_life || 0) > 0) {
      patch.expire_date = ymd(dayjs(d).add(Number(r.shelf_life), "day"));
    }
    updateItem(r._key, patch);
  };

  const payload = () => {
    const v = form.getFieldsValue();
    return {
      fhcode: header.fhcode,
      pdcode: v.pdcode,
      fhdate: ymd(v.fhdate),
      remark: v.remark || null,
      items: items.map((r) => ({
        fg_code: r.fg_code,
        fg_name: r.fg_name,
        jocode: r.jocode,
        unit: r.unit,
        plan_qty: Number(r.plan_qty || 0),
        qty: Number(r.qty || 0),
        mfg_date: r.mfg_date || null,
        expire_date: r.expire_date || null,
        remark: r.remark || null,
      })),
    };
  };

  const validate = (forConfirm) => {
    if (!form.getFieldValue("pdcode")) return "กรุณาเลือก Production Order";
    if (items.length === 0) return "ยังไม่มีรายการ FG";
    if (forConfirm) {
      if (!items.some((r) => Number(r.qty || 0) > 0)) return "ยังไม่มีรายการที่ส่งมอบ (จำนวนเป็น 0 ทั้งหมด)";
      const miss = items.find((r) => Number(r.qty || 0) > 0 && (!r.mfg_date || !r.expire_date));
      if (miss) return `${miss.fg_code} ยังไม่ระบุวันผลิต / วันหมดอายุ`;
    }
    return null;
  };

  const handleSave = () => {
    const e = validate(false);
    if (e) return message.warning(e);
    setSaving(true);
    const req = !header.fhcode ? fhService.create(payload()) : fhService.update(payload());
    req
      .then((res) => {
        applyLoaded(res.data?.data);
        message.success("บันทึกแล้ว (รอยืนยันรับเข้าคลัง)");
      })
      .catch((err) => message.error(errMsg(err, "บันทึกไม่สำเร็จ")))
      .finally(() => setSaving(false));
  };

  const handleConfirm = async () => {
    const e = validate(true);
    if (e) return message.warning(e);
    setSaving(true);
    try {
      let fhcode = header.fhcode;
      if (!fhcode) {
        const res = await fhService.create(payload());
        applyLoaded(res.data?.data);
        fhcode = res.data?.data?.header?.fhcode;
      }
      const res = await fhService.confirm({ ...payload(), fhcode });
      applyLoaded(res.data?.data);
      message.success("รับ FG เข้าคลังเรียบร้อย");
    } catch (err) {
      Modal.error({
        title: "รับเข้าคลังไม่สำเร็จ",
        content: <div style={{ whiteSpace: "pre-line" }}>{errMsg(err, "เกิดข้อผิดพลาด")}</div>,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelDoc = () =>
    fhService
      .cancel(header.fhcode)
      .then((res) => {
        applyLoaded(res.data?.data);
        message.success("ยกเลิกใบส่งมอบแล้ว");
      })
      .catch((err) =>
        Modal.error({
          title: "ยกเลิกไม่สำเร็จ",
          content: <div style={{ whiteSpace: "pre-line" }}>{errMsg(err, "เกิดข้อผิดพลาด")}</div>,
        })
      );

  const total = useMemo(() => items.reduce((t, r) => t + Number(r.qty || 0), 0), [items]);

  const columns = [
    { title: "ลำดับ", key: "no", width: 60, align: "center", render: (_, __, i) => i + 1 },
    { title: "รหัส FG", dataIndex: "fg_code", key: "fg_code", width: 130 },
    { title: "ชื่อสินค้า", dataIndex: "fg_name", key: "fg_name" },
    { title: "Job Order", dataIndex: "jocode", key: "jocode", width: 110 },
    { title: "สั่งผลิต", dataIndex: "plan_qty", key: "plan_qty", width: 100, align: "right", render: num },
    ...(isCreate && !header.fhcode
      ? [{ title: "ส่งมอบไปแล้ว", dataIndex: "sent_qty", key: "sent_qty", width: 110, align: "right", render: (v) => (Number(v) > 0 ? num(v) : "-") }]
      : []),
    {
      title: "ส่งมอบ",
      dataIndex: "qty",
      key: "qty",
      width: 130,
      align: "right",
      render: (v, r) =>
        editable ? (
          <InputNumber
            size="small"
            min={0}
            style={{ width: "100%" }}
            value={Number(v || 0)}
            onChange={(val) => updateItem(r._key, { qty: Number(val || 0) })}
          />
        ) : (
          num(v)
        ),
    },
    { title: "หน่วย", dataIndex: "unit", key: "unit", width: 70, align: "center" },
    {
      title: "วันผลิต",
      dataIndex: "mfg_date",
      key: "mfg_date",
      width: 140,
      render: (v, r) =>
        editable ? (
          <DatePicker size="small" style={{ width: "100%" }} value={toDay(v)} onChange={(d) => onMfgChange(r, d)} />
        ) : (
          fdate(v)
        ),
    },
    {
      title: "วันหมดอายุ",
      dataIndex: "expire_date",
      key: "expire_date",
      width: 140,
      render: (v, r) =>
        editable ? (
          <DatePicker size="small" style={{ width: "100%" }} value={toDay(v)} onChange={(d) => updateItem(r._key, { expire_date: ymd(d) })} />
        ) : (
          fdate(v)
        ),
    },
    ...(status !== FH_PENDING
      ? [{ title: "Lot No.", dataIndex: "lot_no", key: "lot_no", width: 140 }]
      : []),
    ...(editable
      ? [{
          title: "",
          key: "del",
          width: 50,
          align: "center",
          render: (_, r) => (
            <Button type="text" danger size="small" icon={<RiDeleteBin5Line />}
              onClick={() => setItems((prev) => prev.filter((x) => x._key !== r._key))} />
          ),
        }]
      : []),
  ];

  return (
    <Space direction="vertical" size="middle" style={{ display: "flex" }}>
      <Flex justify="space-between" align="center" wrap gap={8}>
        <Space align="center">
          <Typography.Title level={3} className="m-0 !text-zinc-800">
            {config.title || "ใบส่งมอบ FG"} {header.fhcode ? `: ${header.fhcode}` : ""}
          </Typography.Title>
          {header.fhcode && <StatusTag value={status} />}
        </Space>
        <Space wrap>
          <Button style={ACTION_BTN} className="bn-center justify-center" icon={<ArrowLeftOutlined />} onClick={goBack}>
            ย้อนกลับ
          </Button>
          <ActionGroup type="action">
            {editable && (
              <Popconfirm
                placement="bottomRight"
                title="ยืนยันรับ FG เข้าคลัง?"
                description="FG จะเข้าสต๊อกตามจำนวนส่งมอบ หลังยืนยันแล้วแก้ไขรายการไม่ได้"
                okText="ยืนยันรับเข้าคลัง"
                cancelText="ยังก่อน"
                onConfirm={handleConfirm}
              >
                <Button style={{ ...ACTION_BTN, borderColor: "#389e0d", color: "#fff", background: "#389e0d" }} className="bn-center justify-center" icon={<CheckCircleOutlined />} loading={saving}>
                  ยืนยันรับเข้าคลัง
                </Button>
              </Popconfirm>
            )}
            {header.fhcode && status !== FH_CANCELLED && config.action !== "view" && (
              <Popconfirm
                placement="bottomRight"
                title="ยกเลิกใบส่งมอบนี้?"
                description={status === FH_CONFIRMED ? "ระบบจะถอน FG ออกจากคลัง (ถ้ายังไม่ถูกตัดออก)" : undefined}
                okText="ยกเลิกใบส่งมอบ"
                cancelText="ไม่"
                onConfirm={handleCancelDoc}
              >
                <Button danger style={ACTION_BTN} className="bn-center justify-center" icon={<StopOutlined />}>
                  ยกเลิกใบส่งมอบ
                </Button>
              </Popconfirm>
            )}
          </ActionGroup>
          {editable && (
            <Button type="primary" style={ACTION_BTN} className="bn-center justify-center" icon={<SaveFilled />} loading={saving} onClick={handleSave}>
              บันทึก (รอยืนยัน)
            </Button>
          )}
        </Space>
      </Flex>

      <Card size="small">
        <Form form={form} layout="vertical" disabled={!editable} autoComplete="off">
          <Row gutter={[12, 0]}>
            <Col xs={24} md={8}>
              <Form.Item
                label="Production Order"
                name="pdcode"
                rules={[{ required: true }]}
                extra={isCreate && !header.fhcode ? "แสดงเฉพาะ PD ที่เบิกวัตถุดิบ (ตัดสต๊อก) แล้ว" : undefined}
              >
                <Select
                  size="large"
                  showSearch
                  placeholder="พิมพ์เลขที่ PD"
                  options={pdOptions}
                  filterOption={false}
                  onSearch={searchPd}
                  onFocus={() => pdOptions.length <= 1 && searchPd("")}
                  disabled={!isCreate || !!header.fhcode}
                  onChange={loadPrefill}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="วันที่ส่งมอบ" name="fhdate">
                <DatePicker size="large" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item label="หมายเหตุ" name="remark">
                <Input size="large" placeholder="หมายเหตุ" />
              </Form.Item>
            </Col>
          </Row>
          {header.jocodes && (
            <Typography.Text type="secondary">Job Order : {header.jocodes}</Typography.Text>
          )}
          {header.confirmed_date && (
            <div>
              <Tag color="green" className="mt-2">
                รับเข้าคลังเมื่อ {dayjs(header.confirmed_date).format("DD/MM/YYYY HH:mm")}
              </Tag>
            </div>
          )}
        </Form>
      </Card>

      <Card
        size="small"
        title="รายการ FG ที่ส่งมอบ"
        extra={
          editable && (
            <Space wrap>
              {isCreate && !header.fhcode && (
                <Button size="small" icon={<ReloadOutlined />} onClick={() => loadPrefill(form.getFieldValue("pdcode"))}>
                  ดึงรายการจาก PD ใหม่
                </Button>
              )}
              <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setOpenPicker(true)}>
                เพิ่ม FG
              </Button>
            </Space>
          )
        }
      >
        <Table
          size="small"
          rowKey="_key"
          columns={columns}
          dataSource={items}
          pagination={false}
          scroll={{ x: "max-content" }}
          locale={{ emptyText: "เลือก Production Order เพื่อดึงรายการ FG" }}
          footer={() =>
            items.length > 0 && (
              <Flex justify="end">
                <Typography.Text>
                  รวมส่งมอบ <b>{num(total)}</b>
                </Typography.Text>
              </Flex>
            )
          }
        />
      </Card>
      <ModalItemPicker
        open={openPicker}
        onClose={() => setOpenPicker(false)}
        title="เลือก FG"
        fetchItems={() =>
          OptionService()
            .optionsMaterial({})
            .then((res) =>
              (res.data?.data || [])
                .filter((m) => m.typename === "Finished Goods")
                .map((m) => ({ stcode: m.mat_code, stname: m.mat_name, typename: m.typename, unit: m.unit }))
            )
        }
        onSelect={addFg}
      />
    </Space>
  );
}
